import { Award, Crosshair, Frown, Target, Trophy, Repeat2 } from "lucide-react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useLeagueAnalytics } from "@/modules/leagues/hooks/useLeagueAnalytics";
import { Card, CardContent } from "@/components/atoms/Card";
import { Skeleton } from "@/components/atoms/Skeleton";
import { EmptyState } from "@/components/organisms/EmptyState";
import { TrophyIllustration } from "@/components/atoms/illustrations";

/**
 * Estatísticas ANALÍTICAS da liga (itens 24-27 da revisão final) — o
 * ranking em si saiu daqui e virou a aba própria "Ranking" (item 19).
 * Tudo calculado a partir do MESMO dado gravado pelo cron nos
 * participantes (item 36 — fonte única), nunca de outra liga (item 33 —
 * `useLeagueContext` já garante que só olhamos a liga atual).
 */
export function LeagueStatisticsPage() {
  const { league } = useLeagueContext();
  const { data: analytics, isLoading } = useLeagueAnalytics(league.id);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const hasAnyData =
    analytics &&
    (analytics.taxaAcertoGrupo !== null ||
      analytics.mostPredictedScore !== null ||
      analytics.recordistaRodada ||
      analytics.reiDasCravadas ||
      analytics.maisRegular ||
      analytics.rodadaMaisDificil ||
      analytics.top5.length > 0);

  if (!hasAnyData) {
    return (
      <EmptyState
        illustration={TrophyIllustration}
        title="Ainda sem estatísticas"
        description="Assim que as primeiras partidas forem pontuadas, as estatísticas da liga aparecem aqui."
      />
    );
  }

  return (
    <div className="space-y-4">
      {analytics!.taxaAcertoGrupo !== null && (
        <Card className="border-0 bg-gradient-to-br from-primary-50 to-white text-center dark:from-primary-900/30 dark:to-card">
          <CardContent className="p-5">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Taxa de acerto do grupo</p>
            <p className="font-display text-3xl font-bold text-primary">{analytics!.taxaAcertoGrupo}%</p>
          </CardContent>
        </Card>
      )}

      {analytics!.mostPredictedScore && (
        <Card className="border-0 bg-gradient-to-br from-accent-50 to-white text-center dark:from-primary-900/30 dark:to-card">
          <CardContent className="p-5">
            <p className="mb-1 flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
              <Repeat2 className="h-3.5 w-3.5" /> Placar mais palpitado
            </p>
            <p className="font-display text-3xl font-bold tabular-nums">
              {analytics!.mostPredictedScore.home} × {analytics!.mostPredictedScore.away}
            </p>
            <p className="text-xs text-muted-foreground">{analytics!.mostPredictedScore.count}x</p>
          </CardContent>
        </Card>
      )}

      {analytics!.top5.length > 0 && (
        <Card className="border-0 shadow-soft">
          <CardContent className="p-4">
            <p className="mb-2 flex items-center gap-1.5 font-display text-sm font-semibold">
              <Trophy className="h-4 w-4 text-accent-600 dark:text-accent-300" /> Pódio da temporada
            </p>
            <div className="space-y-1.5">
              {analytics!.top5.map((row, idx) => (
                <div key={row.nome} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {idx + 1}º <span className="text-foreground">{row.nome}</span>
                  </span>
                  <span className="font-display font-bold tabular-nums">{row.pontos}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3">
        {analytics!.recordistaRodada && (
          <Card className="border-0 shadow-soft">
            <CardContent className="p-3.5">
              <Award className="mb-1.5 h-4 w-4 text-accent-600 dark:text-accent-300" />
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Recordista de rodada</p>
              <p className="truncate text-sm font-semibold">{analytics!.recordistaRodada.nome}</p>
              <p className="text-xs text-muted-foreground">
                {analytics!.recordistaRodada.pontos} pts · Rodada {analytics!.recordistaRodada.rodada}
              </p>
            </CardContent>
          </Card>
        )}

        {analytics!.reiDasCravadas && (
          <Card className="border-0 shadow-soft">
            <CardContent className="p-3.5">
              <Crosshair className="mb-1.5 h-4 w-4 text-primary" />
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Rei das cravadas</p>
              <p className="truncate text-sm font-semibold">{analytics!.reiDasCravadas.nome}</p>
              <p className="text-xs text-muted-foreground">{analytics!.reiDasCravadas.quantidade} placares exatos</p>
            </CardContent>
          </Card>
        )}

        {analytics!.maisRegular && (
          <Card className="border-0 shadow-soft">
            <CardContent className="p-3.5">
              <Target className="mb-1.5 h-4 w-4 text-success" />
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Mais regular</p>
              <p className="truncate text-sm font-semibold">{analytics!.maisRegular.nome}</p>
              <p className="text-xs text-muted-foreground">~{analytics!.maisRegular.media} pts/rodada</p>
            </CardContent>
          </Card>
        )}

        {analytics!.rodadaMaisDificil && (
          <Card className="border-0 shadow-soft">
            <CardContent className="p-3.5">
              <Frown className="mb-1.5 h-4 w-4 text-destructive" />
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Rodada mais difícil</p>
              <p className="text-sm font-semibold">Rodada {analytics!.rodadaMaisDificil.rodada}</p>
              <p className="text-xs text-muted-foreground">Média de {analytics!.rodadaMaisDificil.media} pts</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
